import { useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { ENV } from '../env';
import { getInitialSession, supabase } from '../io/supabase/client';
import { AppShell } from './shell/AppShell';
import { FilterBar } from './shell/FilterBar';
import { SnapshotButton } from './shell/SnapshotButton';
import { ShareButton } from './shell/ShareButton';
import { UnitPicker } from './shell/UnitPicker';
import { UnitNotice } from './shell/UnitNotice';
import { ProGateChecking, ProUpgrade } from './shell/ProUpgrade';
import { isSectionProGated } from './shell/tabs';
import { useEntitlement } from './state/useEntitlement';
import { TradeActions, type TradesIntent } from './shell/TradeActions';
import { LogButton } from './shell/LogButton';
import { SnapshotProvider } from './state/snapshot';
import { ShareHost } from './state/share';
import { SharedReport } from './share/SharedReport';
import { shareTokenFrom } from '../selectors/share';
import { JournalProvider } from './state/journal';
import { TabMemoryProvider } from './state/tabMemory';
import { PreferencesProvider, usePreferences } from './state/preferences';
import { AccountsProvider } from './state/accounts';
import { BacktestsProvider } from './state/backtests';
import { PracticeProvider } from './state/practice';
import { BestOppsTab } from './tabs/BestOpps';
import { ReviewTab } from './tabs/Review';
import { CommissionsProvider } from './state/commissions';
import { ContractsProvider } from './state/contracts';
import { FiltersProvider } from './state/filters';
import { PatternsProvider } from './state/patterns';
import { ExitModelsProvider } from './state/exitModels';
import { RangeProvider } from './state/range';
import { SupabaseProvider } from './state/supabase';
import { TradeFeedProvider } from './state/tradeFeed';
import { DemoProvider, useDemo } from './state/demo';
import { DemoBanner } from './shell/DemoBanner';
import { DashboardTab } from './tabs/Dashboard';
import { AnalyticsTab } from './tabs/Analytics';
import { AnalyticsSetupTab } from './tabs/AnalyticsSetup';
import { AnalyticsExcursionTab } from './tabs/AnalyticsExcursion';
import { AnalyticsCustomTab } from './tabs/AnalyticsCustom';
import { JournalTab } from './tabs/Journal';
import { PendingDeletionBanner } from './PendingDeletionBanner';
import { CoachReportTab } from './tabs/CoachReport';
import { MyAccountTab } from './tabs/MyAccount';
import { SettingsTab } from './tabs/Settings';
import { AccountsTab, type AccountsIntent } from './tabs/Accounts';
import { TradesTab } from './tabs/Trades';
import { CalendarTab } from './tabs/Calendar';
import { DayOverviewTab } from './tabs/DayOverview';
import { PatternsTab } from './tabs/Patterns';
import { PropReportTab } from './tabs/PropReport';
import { BacktestsTab } from './tabs/Backtests';
import { ResultsTab } from './tabs/Results';
import { PracticeTab } from './tabs/Practice';
import { SignIn } from './auth/SignIn';
import { ResetPassword } from './auth/ResetPassword';
import { isRecoveryPending, markRecoveryPending } from './auth/recovery';
// ⚠ ORDER MATTERS. Tokens define the variables everything else reads; components
// relocate the old client's shared rules; `trades.css` is the first tab-scoped
// pass; `app.css` holds the frame and the tabs still awaiting theirs, so it comes
// last and can override where a tab has not been done yet.
import './tokens.css';
import './components.css';
import './shell.css';
import './trades.css';
import './patterns.css';
import './prop.css';
import './account.css';
import './accounts.css';
import './coach.css';
import './edu.css';
import './backtests.css';
import './results.css';
import './practice.css';
import './bestopps.css';
import './review.css';
import './share.css';
import './app.css';

/**
 * The signed-in application.
 *
 * Phase 0's job here was deliverable A's gate — prove this client can reach the
 * real beta database with a real session. That is done and stays done; the file
 * is now the actual app: auth gate → providers → chrome → whichever tab is open.
 *
 * ⚠ CORRECTED: the auth form IS on the critical path, and calling it optional
 * was a sequencing error. D5 shares the beta PROJECT, not the browser session —
 * Supabase keeps its session in per-origin localStorage, so being signed in on
 * beta.tradecoach.pro does nothing for localhost:5173. Without SignIn the new
 * client renders one screen to everyone, and Phase 4 is gated on reviewing tabs
 * against the old app on the same data.
 */

/**
 * 🚨 THREE STATES, NOT TWO, AND THE THIRD IS THE POINT.
 *
 * Beta had two and layered the reset form on top as a modal — so every path that
 * skipped the modal skipped the intent with it. A refresh was enough: the token
 * is consumed on first load, `PASSWORD_RECOVERY` never fires again, the session
 * is simply valid, and the user browsed on with the old password still live
 * (#31, reported by a real user).
 *
 * Making it an auth STATE means there is no such path. While a reset is
 * half-finished this gate renders the reset screen and nothing else — no
 * providers, no chrome, no data.
 */
type Status =
  | { kind: 'loading' }
  | { kind: 'signed-out' }
  | { kind: 'recovery'; session: Session }
  | { kind: 'signed-in'; session: Session };

/**
 * 🔴 THE HOST IS RECOGNISED AND ITS ENVIRONMENT WAS NEVER WIRED UP.
 *
 * Today that means exactly one thing: somebody has pointed
 * `app.tradecoach.pro` at this build before the production anon key was filled
 * in at cutover. The previous behaviour was to log a console warning and serve
 * the SHARED BETA DATABASE — so a person who believed they were on production
 * would have been reading beta's data and writing into it, with nothing on
 * screen saying so.
 *
 * ⚠ This is a deployment error, not a user error, and the screen says so rather
 * than offering a Retry that cannot help. It also names the environment, because
 * the person who sees this is the person who can fix it.
 */
function EnvNotConfigured() {
  return (
    <div className="env-stop" role="alert">
      <h1>
        {ENV.reason === 'unknown-host'
          ? 'This address isn’t recognised'
          : 'This environment isn’t configured'}
      </h1>
      <p>
        {ENV.reason === 'unknown-host' ? (
          <>
            This build does not know what <b>{ENV.hostname || 'a local file'}</b> is,
            so it will not guess which database it belongs to.
          </>
        ) : (
          <>
            <b>{ENV.name}</b> is pointed at this build, but its database connection
            was never filled in.
          </>
        )}
      </p>
      <p className="muted">
        Nothing has been loaded and nothing has been changed. The app stops here
        deliberately — the alternative is quietly using a different project’s
        database, which looks like it works.
      </p>
      <p className="muted">
        If you own this deployment: set the anon key for <b>{ENV.name}</b> in{' '}
        <code>src/env.ts</code>, or point this hostname at the environment it
        belongs to.
      </p>
    </div>
  );
}

/**
 * 🚨 THE PUBLIC VIEW, RESOLVED ONCE AT MODULE LOAD.
 *
 * ⚠ Read here rather than in a hook so it cannot change under the app. A
 * shared report is a different PAGE, not a different screen of this one: it has
 * no session, no providers and no chrome, and something that could flip
 * mid-render would mean the signed-in app briefly mounting inside a stranger's
 * page.
 *
 * ⚠ SHAPE-CHECKED. A token goes straight into a URL that is fetched, so a
 * hand-typed one that is not a token never reaches the network.
 */
const SHARE_TOKEN = shareTokenFrom(window.location.search);

export function App() {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    // ⚠ Resolved through one helper so the initial check and the event stream
    // cannot disagree about what a session means. They did in beta: the event
    // path knew about recovery and the boot path did not, which IS the bug.
    const resolve = (session: Session | null): Status => {
      if (!session) return { kind: 'signed-out' };
      return isRecoveryPending() ? { kind: 'recovery', session } : { kind: 'signed-in', session };
    };

    // ⚠ NOTHING IS FETCHED ON A MISCONFIGURED HOST. The screen below renders
    // instead, and the auth listener is never armed — so no request goes out to
    // a database this build has no business talking to.
    if (ENV.misconfigured) return;

    void getInitialSession().then((session) => {
      if (cancelled) return;
      setStatus(resolve(session));
    });
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      // 🚨 THE ONE MOMENT THIS IS KNOWABLE. `PASSWORD_RECOVERY` fires exactly
      // once — on the load that consumes the link — and never again, because
      // the token is stripped from the URL. Recording it here is what lets
      // every later load resolve to the same screen.
      if (event === 'PASSWORD_RECOVERY') markRecoveryPending();
      setStatus(resolve(session));
    });
    return () => {
      cancelled = true;
      data.subscription.unsubscribe();
    };
  }, []);

  // 🔴 BEFORE EVERYTHING. The status never leaves 'loading' on a misconfigured
  // host — the effect above returns early — so this has to come first or the
  // screen would be a spinner that never resolves.
  if (ENV.misconfigured) return <EnvNotConfigured />;

  /**
   * 🚨 THE PUBLIC SHARE VIEW, BEFORE THE AUTH GATE AND BEFORE EVERY PROVIDER.
   *
   * ⚠ A visitor has no session and must not be given one. Rendering this
   * after the gate would show a stranger the sign-in form; rendering it INSIDE
   * the providers would start the app's reads against whatever session happens
   * to be in this browser's storage — which on a shared machine means the report
   * is fetched as somebody else.
   *
   * ⚠ It is deliberately NOT gated on being signed out. An owner opening
   * their own link sees exactly what they sent, which is the only way to check
   * what a link actually publishes. Beta does the same (app/index.html:15766).
   */
  if (SHARE_TOKEN) return <SharedReport token={SHARE_TOKEN} />;

  if (status.kind === 'loading') return <Splash>Checking session…</Splash>;

  if (status.kind === 'signed-out') {
    return (
      <main className="app-splash">
        <h1>TRADECOACH</h1>
        <SignIn />
      </main>
    );
  }

  if (status.kind === 'recovery') {
    return (
      <main className="app-splash">
        <h1>TRADECOACH</h1>
        <ResetPassword />
      </main>
    );
  }

  /**
   * 🚨 THE DEMO SITS ABOVE EVERY DATA PROVIDER, AND THAT IS THE WHOLE DESIGN.
   *
   * Entering a demo swaps the CLIENT (see `io/demo/client.ts`). Every provider
   * below takes `sb` as a prop and keys its load effect on it, so one changed
   * identity reloads the entire app — the old client's whole-state swap, reached
   * by construction rather than by wiring each provider to know about demos.
   * There is no provider anyone can forget to convert.
   */
  return (
    <DemoProvider
      real={supabase}
      userId={status.session.user.id}
      today={todayIso(new Date())}
    >
      <AppData userId={status.session.user.id} email={status.session.user.email ?? status.session.user.id} />
    </DemoProvider>
  );
}

/** Today, by the wall clock — the demo book's window anchor. */
const todayIso = (now: Date): string =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

function AppData({ userId, email }: { userId: string; email: string }) {
  const { client } = useDemo();
  // ⚠ `client` is null when no demo is running, and that means "keep the real
  // one" rather than "use nothing".
  const sb = client ?? supabase;

  return (
    <SupabaseProvider sb={sb} userId={userId}>
      {/* ⚠ OUTSIDE EVERYTHING ELSE, because it has to outlive every tab (TCR-95).
          It owns the ONE timer that notices trades ATAS pushed into an already
          open tab; eleven tabs call `useTradeRows`, and a poller mounted any
          lower would start and stop with whichever of them is on screen. It
          needs only the client and the user, so nothing forces it deeper. */}
      <TradeFeedProvider sb={sb} userId={userId}>
      <PreferencesProvider sb={sb} userId={userId}>
        {/* ⚠ RangeProvider sits INSIDE PreferencesProvider on purpose: the range
            anchor is mode-dependent, and in session mode it is the current
            session date rather than the calendar date. */}
        <RangeProvider>
        <FiltersProvider>
          <ContractsProvider sb={sb}>
            <CommissionsProvider sb={sb}>
            <PatternsProvider sb={sb}>
            {/* Beside patterns because the trade form needs BOTH: it refuses to
                save a trade that has a pattern and no exit model, so a picker
                that cannot be populated is a save that cannot go through. */}
            <ExitModelsProvider sb={sb}>
            <AccountsProvider sb={sb} userId={userId}>
              <BacktestsProvider sb={sb} userId={userId}>
              <PracticeProvider sb={sb} userId={userId}>
              <JournalProvider sb={sb}>
                {/* ⚠ Must wrap BOTH the toolbar and the tabs: the camera button
                    is in the shell and the region it captures is in a tab, and
                    this context is the only thing joining them. */}
                <SnapshotProvider>
                  {/* ⚠ INSIDE the camera's provider and outside nothing else:
                      sharing needs the same two halves joined — a button in the
                      shell and a report in a tab — and it additionally reads
                      preferences, accounts, patterns and exit models to resolve
                      the ids in its payload into names a stranger can read. */}
                  <ShareHost>
                    {/* ⚠ ABOVE THE TABS, which is the whole point — a tab is
                        destroyed on every switch, so anything it should
                        remember has to be held by something that is not.
                        See `state/tabMemory.tsx`; the obvious fix (the shell's
                        `key`) was measured and changes nothing. */}
                    <TabMemoryProvider>
                      <SignedIn email={email} />
                    </TabMemoryProvider>
                  </ShareHost>
                </SnapshotProvider>
              </JournalProvider>
              </PracticeProvider>
              </BacktestsProvider>
            </AccountsProvider>
            </ExitModelsProvider>
            </PatternsProvider>
            </CommissionsProvider>
          </ContractsProvider>
        </FiltersProvider>
        </RangeProvider>
      </PreferencesProvider>
      </TradeFeedProvider>
    </SupabaseProvider>
  );
}

function SignedIn({ email }: { email: string }) {
  // 🚨 THE ONLY EXIT FROM DEMO MODE. It is passed into the shell rather than
  // rendered inside a tab, so it survives every navigation and never scrolls
  // away under a 150-row table — see `DemoBanner`.
  const demo = useDemo();
  // ⚠ Read here rather than inside AppShell, so the shell stays free of data
  // concerns: it takes `hidden` as a prop and knows nothing about where
  // preferences come from. That is what let its whole rule set be tested
  // without a provider.
  const { prefs } = usePreferences();

  // The day the Calendar asked for. Owned HERE — see the note at the render
  // prop below for why it cannot live any lower.
  const [pendingDay, setPendingDay] = useState<string | null>(null);

  /**
   * What the toolbar asked the Trades tab to do — open the blank form, or the
   * import dropzone.
   *
   * ⚠ OWNED HERE FOR EXACTLY THE REASON `pendingDay` IS. The shell remounts the
   * tab on `key={tabId}`, so anything held below this line is destroyed by the
   * very `navigate('trades')` call that needs it. That defect already shipped
   * once with the Calendar's day link and opened Day Overview on today, every
   * time, silently.
   *
   * ⚠ AND IT IS CLEARED ONCE CONSUMED, which `pendingDay` does not need to be.
   * A date can be re-applied harmlessly — it is the same day. An intent cannot:
   * left set, walking Trades → Dashboard → Trades by hand would remount the tab
   * and pop a blank trade form the user never asked for, on a tab they were only
   * passing through.
   */
  const [tradesIntent, setTradesIntent] = useState<TradesIntent | null>(null);
  /** Same contract as `tradesIntent`, for the Dashboard's first-account button. */
  const [accountsIntent, setAccountsIntent] = useState<AccountsIntent | null>(null);

  // ⚠ The `.app-root` / `.app-header` strip that used to wrap this is GONE. The
  // old client has no such bar — its brand, build and account info live in the
  // sidebar — and once the shell became a `height: 100vh` grid, a sibling header
  // above it pushed the whole app off-screen. The env label and email moved into
  // the sidebar foot, which is where they are in the thing we are matching.
  return (
    <AppShell
      hidden={prefs?.hiddenTabs}
      banner={<DemoBanner active={demo.active} onExit={demo.exit} />}
      toolbar={(tabId, navigate) => (
        <>
          <FilterBar tabId={tabId} />
          {/* ⚠ IMMEDIATELY AFTER THE FILTERS, which is beta's order (app/index.html:3579).
              The filters say WHICH trades; this says what unit their numbers are
              in. Both are scope, and beta groups them for that reason. */}
          <UnitPicker />
          {/* ⚠ After the filter controls, matching the old client's header
              order: the filters describe WHAT you are looking at, and the camera
              acts on it. */}
          <SnapshotButton />
          {/* ⚠ IMMEDIATELY AFTER THE CAMERA, which is beta's order
              (app/index.html:3580-3584). The two act on the same three tabs and
              differ only in where the picture goes — one to a file on your
              machine, one to a URL anyone holding it can open — so they belong
              side by side and in that order. */}
          <ShareButton />
          {/* ⚠ Beta's order puts the Log between Search and Import. Search is
              not ported, so this sits where it will still be right when it is. */}
          <LogButton />
          {/* ⚠ LAST, as in the old client, and they are the only two of its nine
              header controls whose screens already exist here — see #51 for the
              other five and TradeActions.tsx for why these navigate.
              ⚠ The intent is set BEFORE navigating: the tab remounts on the
              navigate, so it must already be able to see what it was asked to
              do by the time it mounts. */}
          <TradeActions
            onIntent={(intent) => { setTradesIntent(intent); navigate('trades'); }}
          />
        </>
      )}
      meta={<div>{ENV.config.label} · {email}</div>}
    >
      {/* ⚠ 🚨 `pendingDay` LIVES HERE, NOT IN `TabContent`, AND THAT IS THE WHOLE
          TRICK. `AppShell` wraps the tab in `<div className="tab-section"
          key={tabId}>`, so switching tabs REMOUNTS everything below it — and the
          first version of this held the requested date inside `TabContent`,
          where `navigate('dayview')` destroyed it in the same commit that used
          it. The Day Overview opened on today, every time, silently.

          Caught by clicking a calendar cell in the running app. No test would
          have: mounting `DayOverviewTab` directly with an `initialDate` works
          perfectly, and the defect lives entirely in who owns the state. */}
      {(tabId, navigate) => (
        <>
          {/* ⚠ ABOVE EVERY TAB, not on My Account only. A scheduled deletion is
              invisible — the app looks completely normal and thirty days later
              it is all gone — so the warning has to be somewhere the user
              cannot fail to pass. It renders nothing at all when none is
              pending, which is almost always. */}
          <PendingDeletionBanner />
          {/* ── 🚨 THE TAB SAYS WHEN THE UNIT SWITCH DOES NOT REACH IT ──

              A global control that silently does nothing on most screens is worse
              than no control: the reader cannot tell "this tab is in dollars" from
              "this tab ignored me", and the second reading is the one that makes
              them distrust a figure that is correct.

              ⚠ A tab counts as converted only when its SELECTOR and its FORMATTERS
              moved together. Half-converting is the one genuinely dangerous state —
              the selector returns points and `money()` prints them with a dollar
              sign, which on a $50-a-point contract is a plausible-looking figure out
              by 50x.

              ⚠ HERE RATHER THAN IN `AppShell`, which has no other reason to read
              preferences and whose own test mounts it without a provider —
              `usePreferences()` throws outside one by design, and that guard is
              worth keeping.

              ▶ The list inside is the progress record for #44, kept beside the
              screen it describes so the two cannot drift. Remove a tab from it in
              the same commit that converts that tab. */}
          <UnitNotice tabId={tabId} />
          {/* ⚠ `go` is built HERE because `navigate` and the intent setter live
              here. It arms the Trades tab BEFORE navigating, the same order the
              toolbar uses and for the same reason — the tab remounts on arrival,
              so it must already know what it was asked to do by then. */}
          <TabContent
            tabId={tabId}
            pendingDay={pendingDay}
            openDay={(date) => { setPendingDay(date); navigate('dayview'); }}
            tradesIntent={tradesIntent}
            onTradesIntentHandled={() => setTradesIntent(null)}
            accountsIntent={accountsIntent}
            onAccountsIntentHandled={() => setAccountsIntent(null)}
            go={(tab, intent) => {
              // ⚠ ROUTED BY VALUE, not by tab, so a caller cannot arm the wrong
              // screen by naming a tab that does not own that intent.
              if (intent === 'new') setAccountsIntent(intent);
              else if (intent) setTradesIntent(intent);
              navigate(tab);
            }}
          />
        </>
      )}
    </AppShell>
  );
}

/**
 * The port ledger.
 *
 * ⚠ An unported tab SAYS SO rather than rendering an empty screen. A blank tab
 * during a phased port is indistinguishable from a broken one, and the person
 * checking parity is exactly the one who needs to tell them apart.
 */
/**
 * ── 🚨 THE DEEP LINK FROM THE CALENDAR, AND WHY IT IS A `key` ──
 *
 * Clicking a day on the Calendar has to open the Day Overview ON THAT DAY. The
 * Day Overview owns its date internally — it steps, and it snaps weekends — so
 * the date cannot simply become a prop without inverting all of that.
 *
 * Handing it as `initialDate` plus a `key` REMOUNTS the tab, which seeds its
 * `useState` with the requested day and leaves every other behaviour alone. The
 * alternative — an effect that writes the requested date into the tab's state
 * after render — is D86 exactly: a fallback render plus a seeding effect is a
 * race, and the loser paints one day under another day's heading.
 *
 * ⚠ `pendingDay` is owned by `App`, above the shell's `key={tabId}` remount.
 * See the note at that call site; holding it here does not work.
 */
export function TabContent(
  { tabId, pendingDay, openDay, tradesIntent, onTradesIntentHandled,
    accountsIntent, onAccountsIntentHandled, go }:
  {
    tabId: string;
    /** Send the reader to another tab, optionally arming Trades or Accounts. */
    go: (tab: string, intent?: TradesIntent | AccountsIntent) => void;
    /**
     * Set by the Dashboard's "+ Create your first account".
     * ⚠ OPTIONAL so existing callers — and the gating tests, which render this
     * component directly to exercise the paywall — need no change to keep
     * compiling. A caller that never arms Accounts behaves exactly as before.
     */
    accountsIntent?: AccountsIntent | null | undefined;
    onAccountsIntentHandled?: (() => void) | undefined;
    pendingDay: string | null;
    openDay: (date: string) => void;
    /** Set by the toolbar's Import / Add Trade. See the note where it is owned. */
    tradesIntent: TradesIntent | null;
    onTradesIntentHandled: () => void;
  },
) {
  // ── 🚨 THE PAYWALL, AND THE ONE PLACE IT CAN LIVE ──
  //
  // Nine tabs have carried `gate: 'pro'` since the shell was ported and NOTHING
  // READ IT — every Pro screen rendered in full to everybody. Invisible, because
  // the old client's own gate is switched off too, so both clients agreed and
  // neither a screenshot comparison nor the differential harness could see it.
  //
  // ⚠ Called unconditionally, above every branch, because it is a hook. It is
  // also the reason the gate goes HERE rather than in `resolveTab`: the tab must
  // stay reachable so the reader lands on the pitch instead of being bounced.
  //
  // ⚠ INERT TODAY. `PAID_LAUNCH` is false, so `pro` is true and `pending` is
  // false for everyone, and both lines below return nothing — which is exactly
  // the parity the phase is gated on.
  //
  // 🚨 `pending` comes FIRST and is not a spinner for its own sake. The read is
  // async and unknown fails open, so without it a locked tab renders its real
  // content for the frame before the answer lands — measured: the Excursion tab
  // mounted and began loading. See ui/state/useEntitlement.ts.
  const { pro, pending } = useEntitlement();
  if (isSectionProGated(tabId)) {
    if (pending) return <ProGateChecking />;
    if (!pro) return <ProUpgrade tabId={tabId} go={go} />;
  }

  if (tabId === 'dashboard') return <DashboardTab go={go} />;
  if (tabId === 'journal') return <JournalTab />;
  // ⚠ All five analytics sub-tabs are routed now. Custom Analytics is PARTIAL —
  // its Build-your-own builder is real and its two curated dashboards name
  // themselves as unported on screen, rather than the tab silently showing one
  // dashboard under the other's name.
  if (tabId === 'analytics-overview') return <AnalyticsTab />;
  if (tabId === 'analytics-setup') return <AnalyticsSetupTab />;
  if (tabId === 'analytics-excursion') return <AnalyticsExcursionTab />;
  if (tabId === 'analytics-custom') return <AnalyticsCustomTab />;
  if (tabId === 'settings') return <SettingsTab />;
  if (tabId === 'accounts') {
    return (
      <AccountsTab
        go={go}
        intent={accountsIntent}
        onIntentHandled={onAccountsIntentHandled}
      />
    );
  }
  if (tabId === 'trades') {
    return <TradesTab intent={tradesIntent} onIntentHandled={onTradesIntentHandled} />;
  }
  if (tabId === 'calendar') return <CalendarTab onOpenDay={openDay} />;
  if (tabId === 'dayview') {
    return <DayOverviewTab key={pendingDay ?? 'today'} initialDate={pendingDay} />;
  }
  if (tabId === 'patterns') return <PatternsTab />;
  if (tabId === 'analytics-prop') return <PropReportTab />;
  if (tabId === 'backtests') return <BacktestsTab />;
  // ⚠ A FOLD-IN, not a relocation — beta serves this from a separate document in
  // an iframe. See ui/tabs/Results.tsx.
  if (tabId === 'backtest-dashboard') return <ResultsTab go={go} />;
  if (tabId === 'practice') return <PracticeTab />;
  if (tabId === 'bestopps') return <BestOppsTab />;
  if (tabId === 'review') return <ReviewTab go={go} />;
  if (tabId === 'account') return <MyAccountTab />;
  if (tabId === 'reports') return <CoachReportTab go={go} />;
  return (
    <div className="card">
      <h3>Not ported yet</h3>
      <p className="muted">
        <code>{tabId}</code> still lives in the current client. This is Phase 4 in
        progress, not a failure — the tab order is in REBUILD_PLAN §7.
      </p>
    </div>
  );
}

function Splash({ children }: { children: ReactNode }) {
  return (
    <main className="app-splash">
      <h1>TRADECOACH</h1>
      <p className="muted">{children}</p>
    </main>
  );
}
